// SVG base map: ocean, NOC grid, the Philippine archipelago and the RouteZero backbone.
// Shapes only — every label and interactive marker lives in the HTML overlay
// (see src/ui/map-view.js) so text stays crisp and accessible at any zoom.

import { LAND, BBOX } from './geo.js';
import { CITIES, BACKBONE, project } from './mapData.js';

const NS = 'http://www.w3.org/2000/svg';
/** Tiny SVG element helper. */
export function s(tag, attrs = {}, ...kids) {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) if (v != null && v !== false) el.setAttribute(k, v);
  for (const c of kids.flat(Infinity)) if (c != null) el.append(c);
  return el;
}

export const WORLD_BOX = { cx: (BBOX[0] + BBOX[2]) / 2, cy: (BBOX[1] + BBOX[3]) / 2, w: BBOX[2] - BBOX[0] + 60, h: BBOX[3] - BBOX[1] + 70 };

/** Quadratic arc between two map points, bowed sideways by `bend` × distance. */
export function arcPath([x1, y1], [x2, y2], bend = 0.18) {
  const mx = (x1 + x2) / 2; const my = (y1 + y2) / 2; const dx = x2 - x1; const dy = y2 - y1;
  return `M${x1},${y1}Q${mx - dy * bend},${my + dx * bend} ${x2},${y2}`;
}

export function baseMap() {
  const [x0, y0, x1, y1] = BBOX;
  const pad = 1600;
  const defs = s('defs', {},
    s('clipPath', { id: 'ph-land-clip' }, s('path', { d: LAND })),
    s('linearGradient', { id: 'ph-ocean', x1: '0', y1: '0', x2: '0', y2: '1' },
      s('stop', { offset: '0', style: 'stop-color: var(--map-ocean-top)' }), s('stop', { offset: '1', style: 'stop-color: var(--map-ocean-bottom)' })),
    s('linearGradient', { id: 'ph-land', x1: '0', y1: '0', x2: '1', y2: '1' },
      s('stop', { offset: '0', style: 'stop-color: var(--map-land-a)' }), s('stop', { offset: '1', style: 'stop-color: var(--map-land-b)' })),
    s('pattern', { id: 'ph-grid', width: 50, height: 50, patternUnits: 'userSpaceOnUse' },
      s('path', { d: 'M50 0H0V50', class: 'map-grid-line' })));
  const ocean = s('rect', { class: 'map-ocean', x: x0 - pad, y: y0 - pad, width: x1 - x0 + pad * 2, height: y1 - y0 + pad * 2, fill: 'url(#ph-ocean)' });
  const grid = s('rect', { class: 'map-grid', x: x0 - pad, y: y0 - pad, width: x1 - x0 + pad * 2, height: y1 - y0 + pad * 2, fill: 'url(#ph-grid)' });
  const shallows = s('path', { class: 'map-shallows', d: LAND });
  const land = s('path', { class: 'map-land', d: LAND, fill: 'url(#ph-land)' });
  return { defs, layers: [ocean, grid, shallows, land] };
}

/** Backbone links between RouteZero facilities; `lit` when both cities are reachable. */
export function backbone(isLit) {
  const pos = Object.fromEntries(CITIES.map((c) => [c.domainId, project(...c.site.at)]));
  return s('g', { class: 'map-backbone' }, BACKBONE.map(([a, b], i) => {
    const lit = isLit(a) && isLit(b);
    return s('path', { class: `bb-link ${lit ? 'lit' : 'dim'}`, d: arcPath(pos[a], pos[b], i % 2 ? -0.16 : 0.16), 'data-link': `${a}-${b}` });
  }));
}
